import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { ICellRendererParams } from 'ag-grid-community';
import { Button, InputGroup } from '@blueprintjs/core';
import { TranslatedMessage } from '../common/translation';
import { SecretariatStore } from '../../store/SecretariatStore';
import { Link } from 'react-router-dom';
import { Page, TLCode } from '../../common/enums';
import { Instance } from 'mobx-state-tree';
import { ApplicationStore, AuthenticationStore } from '../../store';
import styled from '@emotion/styled';
import BaseGrid from '../common/grid/BaseGrid';
import MainSectionLayout from '../layout/MainSectionLayout';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import EditCell from '../common/grid/EditCell';
import {
  Column,
  ColumnChooser,
  DataGrid,
  Button as DGButton,
  DataGridTypes,
  Export,
  FilterPanel,
  FilterRow,
  GroupPanel,
  Grouping,
  HeaderFilter,
  Pager,
  Paging,
  SearchPanel,
  Sorting,
} from 'devextreme-react/data-grid';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import DataSource from 'devextreme/data/data_source';


const ContainerCol = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
const DivLeft = styled.div`
  display: flex;
  flex-direction: column;
  width: 30%;
  margin-right: 5%;
  padding-bottom: 5px;
`;
const DivRight = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 5px;
  .bp3-icon {
    margin-right: 3px;
  }
`;

interface SecretariatListPageProps {
  SecretariatStore?: Instance<typeof SecretariatStore>;
  ApplicationStore?: Instance<typeof ApplicationStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
}

interface SecretariatListPageState {
  isLoading: boolean;
  filter: string;
  columnDefs: any;
}

@inject('SecretariatStore', 'ApplicationStore', 'AuthenticationStore')
@observer
class SecretariatListPage extends React.Component<
  SecretariatListPageProps & WithTranslateProps,
  SecretariatListPageState
> {
  constructor(props: any) {
    super(props);
    this.state = {
      isLoading: false,
      filter: '',
      columnDefs: [
        {
          headerName: this.props.translate(TLCode.SECRETARIAT_Code),
          width: 100,
          filter: false,
          field: 'code',
          sortingOrder: ['asc', 'desc'],
        },
        {
          headerName: this.props.translate(TLCode.SECRETARIAT_Name),
          width: 200,
          filter: false,
          sort: 'asc',
          field: 'name',
        },
        {
          headerName: this.props.translate(TLCode.SECRETARIAT_Address),
          filter: false,
          sort: 'asc',
          field: 'address',
        },
        {
          headerName: this.props.translate(TLCode.SECRETARIAT_MailAddress),
          width: 200,
          filter: false,
          sort: 'asc',
          field: 'email',
        },
        {
          headerName: this.props.translate(TLCode.SECRETARIAT_Language),
          width: 50,
          filter: false,
          field: 'language',
        },
        {
          headerName: '',
          width: 75,
          suppressSorting: true,
          filter: false,
          cellRendererFramework: EditCell,
          cellRendererParams: (params: ICellRendererParams) => {
            return {
              onEditButtonClick: () => {
                this.props.ApplicationStore!.navigate(`${Page.SecretariatPage}/${params.data.id}`);
              },
            };
          },
        },
      ],
    };
  }

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    const { SecretariatStore } = this.props;
    this._isMounted && this.setState({ isLoading: true });
    await SecretariatStore!.checkListLoaded();
    this._isMounted && this.setState({ isLoading: false });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  buildGridData = () => {
    return this.props.SecretariatStore!.dataList.map((secretariat) => {
      return {
        id: secretariat.secretariatId,
        name: secretariat.name,
        code: secretariat.code,
        address: secretariat.address + ' ' + secretariat.zipCode + ' ' + secretariat.city,
        language: secretariat.language,
        email: secretariat.mailAddress
      };
    });
  };

  setFilterText(filter) {
    this.setState({
      filter,
    });
  }

  private onRowClicked = (ev) => {
    this.props.ApplicationStore!.navigate(`${Page.SecretariatPage}/${ev.data.id}`);
  };

  public render() {
    const { isAdmin, isSuperAdmin } = this.props.AuthenticationStore!;
    const { translate } = this.props;

    return (
      <MainSectionLayout isLoading={this.state.isLoading} title={TLCode.SECRETARIATS_Title}>
        <ContainerCol>
          <DivLine>
            <DivLeft>
              <InputGroup
                placeholder="Filter"
                leftIcon="search"
                value={this.state.filter}
                onChange={(e) => this.setFilterText(e.target.value)}
              />
            </DivLeft>
            <DivRight>
              {isAdmin || isSuperAdmin ? (
                <Link to={Page.SecretariatPage}>
                  <Button intent="primary">
                    <TranslatedMessage id={TLCode.Add} />
                  </Button>
                </Link>
              ) : (
                <></>
              )}
            </DivRight>
          </DivLine>
          <DataGrid
            id="secretariatList"
            dataSource={this.buildGridData()}
            //onExporting={this.onExporting}
            showBorders
            allowColumnResizing
            width={'100%'}
            rowAlternationEnabled={true}
            onRowClick={this.onRowClicked}
          >
            <Export enabled={true} texts={{ exportAll: translate(TLCode.GRID_EXCEL_EXPORT) }} />
            <Sorting mode="multiple" />
            <FilterPanel visible={true} />
            <HeaderFilter visible={true} />
            <FilterRow visible={true} applyFilter={'auto'} />
            <SearchPanel visible={true} highlightCaseSensitive={true} width={400} />
            <Column
              dataType="string"
              dataField="code"
              caption={translate(TLCode.SECRETARIAT_Code)}
              width={200}
              defaultSortOrder="asc"
              sortIndex={1}
            />
            <Column
              dataType="string"
              dataField="name"
              caption={translate(TLCode.SECRETARIAT_Name)}
              width={400}
            />
            <Column
              dataType="string"
              dataField="address"
              caption={translate(TLCode.SECRETARIAT_Address)}
              //width={200}
            />
            <Column
              dataType="string"
              dataField="language"
              caption={translate(TLCode.SECRETARIAT_Language)}
              width={50}
            />
            <Column
              caption="Actions"
              type="buttons">
                <DGButton
                hint={translate(TLCode.GENERAL_Edit)}
                icon={'user'}
                onClick={(e) => {
                  this.props.ApplicationStore!.navigate(`${Page.SecretariatPage}/${e.row!.data.id}`)
                  }}
                />
            </Column>
            <Pager
              visible={true}
              allowedPageSizes={[10, 20, 50]}
              showPageSizeSelector={true}
              showInfo={true}
              showNavigationButtons={true}
            />
            <Paging defaultPageSize={20} />
          </DataGrid>
          {/* <BaseGrid
            keyName="id"
            columnDefs={this.state.columnDefs}
            rowData={this.buildGridData()}
            loading={this.state.isLoading}
            quickFilterText={this.state.filter}
            onRowClicked={this.onRowDoubleClicked}
          /> */}
        </ContainerCol>
      </MainSectionLayout>
    );
  }
}

export default withTranslate(SecretariatListPage);
